import React, { useState } from 'react'

import {
    Paper,
    TableCell,
    TableRow,
    TableContainer,
    Table,
    TableHead,
    TableBody,
    useTheme,
    Button,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
} from '@material-ui/core'

import { InternalInvoice } from '../../../models/Invoice/types'
import { formatCurrency, formatDate } from './helpers'

export const PaymentHistoryTable = ({
    invoice,
    viewOnly,
    onVoidPayment,
}: {
    invoice: InternalInvoice
    viewOnly: boolean
    onVoidPayment: (paymentId: string, reason: string) => void
}) => {
    const theme = useTheme()
    const [voidReason, setVoidReason] = useState('')
    const [voidingPaymentId, setVoidingPaymentId] = useState<string | null>(
        null,
    )
    const [voidDialogOpen, setVoidDialogOpen] = useState(false)

    const handleVoidPayment = () => {
        if (voidingPaymentId && voidReason.trim()) {
            onVoidPayment(voidingPaymentId, voidReason.trim())
            setVoidingPaymentId(null)
            setVoidReason('')
            setVoidDialogOpen(false)
        }
    }

    const handleOpenVoidDialog = (paymentId: string) => {
        setVoidingPaymentId(paymentId)
        setVoidReason('')
        setVoidDialogOpen(true)
    }

    const handleCloseVoidDialog = () => {
        setVoidDialogOpen(false)
        setVoidingPaymentId(null)
        setVoidReason('')
    }

    return (
        <>
            <Typography variant="h6" gutterBottom>
                Payment History
            </Typography>
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Date</TableCell>
                            <TableCell>Amount</TableCell>
                            <TableCell>Method</TableCell>
                            <TableCell>Reference</TableCell>
                            <TableCell>Status</TableCell>
                            {!viewOnly && <TableCell>Actions</TableCell>}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {invoice.payments.map((payment) => (
                            <TableRow
                                key={payment.id}
                                style={{
                                    backgroundColor: payment.voided
                                        ? theme.palette.action
                                              .disabledBackground
                                        : 'inherit',
                                    textDecoration: payment.voided
                                        ? 'line-through'
                                        : 'none',
                                }}
                            >
                                <TableCell>
                                    {formatDate(payment.payment_date)}
                                </TableCell>
                                <TableCell>
                                    {formatCurrency(payment.payment_amount)}
                                </TableCell>
                                <TableCell>{payment.payment_method}</TableCell>
                                <TableCell>
                                    {payment.payment_reference}
                                </TableCell>
                                <TableCell>
                                    {payment.voided
                                        ? `Voided - ${payment.void_reason}`
                                        : 'Active'}
                                </TableCell>
                                {!viewOnly && (
                                    <TableCell>
                                        {!payment.voided && !invoice.voided && (
                                            <Button
                                                size="small"
                                                color="secondary"
                                                onClick={() =>
                                                    handleOpenVoidDialog(
                                                        payment.id,
                                                    )
                                                }
                                            >
                                                Void
                                            </Button>
                                        )}
                                    </TableCell>
                                )}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Void Payment Dialog */}
            <Dialog
                open={voidDialogOpen}
                onClose={handleCloseVoidDialog}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Void Payment</DialogTitle>
                <DialogContent>
                    <Typography
                        variant="body2"
                        color="textSecondary"
                        style={{ marginBottom: 16 }}
                    >
                        Please provide a reason for voiding this payment. This
                        action cannot be undone.
                    </Typography>
                    <TextField
                        fullWidth
                        label="Void Reason"
                        value={voidReason}
                        onChange={(e) => setVoidReason(e.target.value)}
                        variant="outlined"
                        multiline
                        rows={3}
                        required
                        helperText="Enter a reason for voiding this payment"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseVoidDialog} color="primary">
                        Cancel
                    </Button>
                    <Button
                        onClick={handleVoidPayment}
                        color="secondary"
                        variant="contained"
                        disabled={!voidReason.trim()}
                    >
                        Void Payment
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}
